import {
  ArrowLeftIcon,
  BanknotesIcon,
  BuildingOfficeIcon,
  CalendarDaysIcon,
  ChatBubbleLeftIcon,
  ClipboardDocumentCheckIcon,
  EnvelopeIcon,
  MapPinIcon,
  PencilIcon,
  PhoneIcon,
  PlusIcon,
  TruckIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import AISalesAssistant from '../../components/ai/AISalesAssistant';
import CustomerModal from '../../components/customers/CustomerModal';
import InterestedVehiclesModal from '../../components/customers/InterestedVehiclesModal';
import DocumentGenerator from '../../components/documents/DocumentGenerator';
import SendEmailModal from '../../components/messaging/SendEmailModal';
import SendSMSModal from '../../components/messaging/SendSMSModal';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import { staffPortalRequestsApi, StaffPortalRequest } from '../../services/staffPortalRequestsApi';
import { formatDisplayAddress } from '../../utils/addressUtils';

interface InterestedVehicle {
  id: string;
  year: number;
  make: string;
  model: string;
  asking_price: number;
  priority_order: number;
  notes?: string;
  primary_image?: string;
}

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  middle_name?: string;
  phone: string;
  email: string;
  work_phone?: string;
  street_address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  date_of_birth?: string;
  ssn_last_four?: string;
  employer?: string;
  job_title?: string;
  monthly_income?: number;
  employment_start_date?: string;
  credit_score?: number;
  credit_tier?: string;
  status: string;
  preferred_contact_method?: string;
  lead_source?: string;
  assigned_salesperson_name?: string;
  created_at: string;
  updated_at?: string;
  notes?: string;
  tags?: string[];
  interested_vehicles: InterestedVehicle[];
}

const CustomerDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isInterestedVehiclesModalOpen, setIsInterestedVehiclesModalOpen] = useState(false);
  const [noteModal, setNoteModal] = useState({
    open: false,
    title: '',
    description: '',
    submitting: false,
  });
  const [followUpModal, setFollowUpModal] = useState({
    open: false,
    title: '',
    dueDate: '',
    submitting: false,
  });
  const [smsModalOpen, setSmsModalOpen] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [portalRequests, setPortalRequests] = useState<StaffPortalRequest[]>([]);

  const fetchCustomer = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get<Customer>(`/customers/${id}`);
      setCustomer(response);
    } catch (error) {
      console.error('Error fetching customer:', error);
      setError('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  useEffect(() => {
    // v2 servicing Slice 11 (D6): per-customer portal-request trace, own
    // independent fetch -- a failure here shouldn't affect the rest of the
    // customer page.
    if (!id) return;
    staffPortalRequestsApi
      .listForCustomer(id)
      .then(setPortalRequests)
      .catch(() => setPortalRequests([]));
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const handleEditCustomer = () => {
    setIsEditModalOpen(true);
  };

  const handleSaveCustomer = async (updatedCustomer: any): Promise<any> => {
    try {
      const savedCustomer = await apiService.put(`/customers/${id}`, updatedCustomer);
      await fetchCustomer(); // Refresh customer data
      return savedCustomer; // Return the saved customer for interested vehicles functionality
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error; // Re-throw to let modal handle error display
    }
  };

  const getStatusBadge = (status: string) => {
    const colors = {
      ACTIVE: 'bg-green-100 text-green-800',
      INACTIVE: 'bg-gray-100 text-gray-800',
      DO_NOT_CALL: 'bg-red-100 text-red-800',
    };
    return colors[status as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Customers
          </button>
        </div>
        <div className="card animate-pulse">
          <div className="h-8 bg-gray-300 rounded mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
            <div className="h-4 bg-gray-300 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/customers')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Customers
          </button>
        </div>
        <div className="card">
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserCircleIcon className="h-6 w-6 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {typeof error === 'string'
                ? error
                : (error as any)?.message || (error as any)?.detail || 'Customer not found'}
            </h3>
            <p className="text-gray-500">
              The customer you're looking for doesn't exist or couldn't be loaded.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/customers')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Customers
        </button>
        <button className="btn-primary" onClick={handleEditCustomer}>
          <PencilIcon className="h-4 w-4 mr-2" />
          Edit Customer
        </button>
      </div>

      {/* Customer Info */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <UserCircleIcon className="h-8 w-8 text-gray-400 mr-3" />
                  <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                      {customer.first_name} {customer.middle_name ? `${customer.middle_name} ` : ''}
                      {customer.last_name}
                    </h1>
                    <div className="flex items-center gap-2 mt-1">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(customer.status)}`}
                      >
                        {customer.status.replace('_', ' ').toUpperCase()}
                      </span>
                      {customer.credit_score && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                          Credit: {customer.credit_score}
                        </span>
                      )}
                      {customer.lead_source && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          {customer.lead_source}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Contact Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Phone</p>
                      <p className="text-sm text-gray-600">{customer.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <EnvelopeIcon className="h-5 w-5 text-gray-400 mr-3" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Email</p>
                      <p className="text-sm text-gray-600">{customer.email}</p>
                    </div>
                  </div>
                  {customer.work_phone && (
                    <div className="flex items-center">
                      <PhoneIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Work Phone</p>
                        <p className="text-sm text-gray-600">{customer.work_phone}</p>
                      </div>
                    </div>
                  )}
                  {customer.preferred_contact_method && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Preferred Contact</p>
                      <p className="text-sm text-gray-600 capitalize">
                        {customer.preferred_contact_method}
                      </p>
                    </div>
                  )}
                  <div className="flex items-start">
                    <MapPinIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Address</p>
                      <p className="text-sm text-gray-600">
                        {formatDisplayAddress(
                          customer.street_address,
                          customer.city,
                          customer.state,
                          customer.zip_code
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
                <div className="space-y-3">
                  {customer.date_of_birth && (
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-3" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Date of Birth</p>
                        <p className="text-sm text-gray-600">
                          {formatDate(customer.date_of_birth)}
                        </p>
                      </div>
                    </div>
                  )}
                  {customer.ssn_last_four && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">SSN</p>
                      <p className="text-sm text-gray-600">***-**-{customer.ssn_last_four}</p>
                    </div>
                  )}
                  {customer.employer && (
                    <div className="flex items-start">
                      <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Employer</p>
                        <p className="text-sm text-gray-600">{customer.employer}</p>
                        {customer.job_title && (
                          <p className="text-xs text-gray-500">{customer.job_title}</p>
                        )}
                      </div>
                    </div>
                  )}
                  {customer.monthly_income && (
                    <div className="flex items-start">
                      <BanknotesIcon className="h-5 w-5 text-gray-400 mr-3 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">Monthly Income</p>
                        <p className="text-sm text-gray-600">
                          ${customer.monthly_income.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}
                  {customer.employment_start_date && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Employment Start</p>
                      <p className="text-sm text-gray-600">
                        {formatDate(customer.employment_start_date)}
                      </p>
                    </div>
                  )}
                  {customer.credit_tier && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Credit Tier</p>
                      <p className="text-sm text-gray-600">{customer.credit_tier}</p>
                    </div>
                  )}
                  {customer.assigned_salesperson_name && (
                    <div>
                      <p className="text-sm font-medium text-gray-900">Assigned Salesperson</p>
                      <p className="text-sm text-gray-600">{customer.assigned_salesperson_name}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tags */}
            {customer.tags && customer.tags.length > 0 && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {customer.tags.map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {customer.notes && (
              <div className="mt-6 pt-6 border-t">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
                <p className="text-sm text-gray-600">{customer.notes}</p>
              </div>
            )}

            {/* Interested Vehicles */}
            <div className="mt-6 pt-6 border-t">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <TruckIcon className="h-5 w-5 text-gray-400 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">Interested Vehicles</h3>
                  {customer.interested_vehicles && customer.interested_vehicles.length > 0 && (
                    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {customer.interested_vehicles.length}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setIsInterestedVehiclesModalOpen(true)}
                  className="btn-secondary text-sm"
                >
                  <PlusIcon className="w-4 h-4 mr-1" />
                  Manage Vehicles
                </button>
              </div>

              {customer.interested_vehicles && customer.interested_vehicles.length > 0 ? (
                <div className="space-y-4">
                  {customer.interested_vehicles
                    .sort((a, b) => a.priority_order - b.priority_order)
                    .map((vehicle) => (
                      <div key={vehicle.id} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <h4 className="text-sm font-semibold text-gray-900">
                                {vehicle.year} {vehicle.make} {vehicle.model}
                              </h4>
                              {vehicle.priority_order && (
                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                  #{vehicle.priority_order}
                                </span>
                              )}
                            </div>
                            {vehicle.asking_price && (
                              <p className="text-lg font-bold text-green-600 mt-1">
                                ${vehicle.asking_price.toLocaleString()}
                              </p>
                            )}
                            {vehicle.notes && (
                              <p className="text-sm text-gray-600 mt-2">{vehicle.notes}</p>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="w-16 h-12 bg-gray-200 rounded-md flex items-center justify-center">
                              {vehicle.primary_image ? (
                                <img
                                  src={apiService.getFileUrl(vehicle.primary_image)}
                                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                                  className="w-full h-full object-cover rounded-md"
                                />
                              ) : (
                                <TruckIcon className="h-6 w-6 text-gray-400" />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="text-gray-500">
                    No interested vehicles yet. Click "Manage Vehicles" to add some.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Activity Timeline */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Activity Timeline</h3>
            </div>
            <div className="space-y-3">
              <div className="text-sm">
                <p className="font-medium text-gray-900">Customer Created</p>
                <p className="text-gray-600">{formatDate(customer.created_at)}</p>
              </div>
              {customer.updated_at && customer.updated_at !== customer.created_at && (
                <div className="text-sm">
                  <p className="font-medium text-gray-900">Last Updated</p>
                  <p className="text-gray-600">{formatDate(customer.updated_at)}</p>
                </div>
              )}
              {portalRequests.length > 0 && (
                <div className="pt-3 mt-3 border-t border-gray-100 space-y-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                    Portal Requests
                  </p>
                  {portalRequests.map((r) => (
                    <div key={r.id} className="text-sm">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-gray-900 truncate">{r.subject}</p>
                        <span className="text-xs text-gray-500 shrink-0 ml-2">{r.status}</span>
                      </div>
                      <p className="text-gray-600 text-xs">
                        {r.request_type} ·{' '}
                        {r.last_message_at
                          ? formatDate(r.last_message_at)
                          : formatDate(r.created_at)}
                      </p>
                      <button
                        onClick={() => navigate(`/bhph/portal-requests?customer_id=${customer.id}`)}
                        className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                      >
                        View thread
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <div className="card-header">
              <h3 className="text-lg font-medium text-gray-900">Quick Actions</h3>
            </div>
            <div className="space-y-2">
              <button
                onClick={() => navigate(`/calculator?customerId=${customer?.id}`)}
                className="w-full btn-secondary text-left"
              >
                Create Deal
              </button>
              <button
                onClick={async () => {
                  try {
                    await apiService.post(`/credit-applications/from-customer/${customer.id}`);
                    navigate('/credit-applications');
                  } catch (err) {
                    console.error('Failed to create credit application:', err);
                  }
                }}
                className="w-full btn-secondary text-left flex items-center"
              >
                <ClipboardDocumentCheckIcon className="h-4 w-4 mr-2" />
                Create Credit Application
              </button>
              <button
                onClick={() => navigate(`/customers/${id}/workspace`)}
                className="w-full btn-primary text-left"
              >
                Open Workspace
              </button>
              <button
                onClick={() =>
                  setNoteModal({ open: true, title: '', description: '', submitting: false })
                }
                className="w-full btn-secondary text-left"
              >
                Add Note
              </button>
              <button
                onClick={() =>
                  setFollowUpModal({ open: true, title: '', dueDate: '', submitting: false })
                }
                className="w-full btn-secondary text-left"
              >
                Schedule Follow-up
              </button>
              <button
                onClick={() => setSmsModalOpen(true)}
                disabled={!customer.phone || customer.status === 'do_not_call'}
                className="w-full btn-secondary text-left flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChatBubbleLeftIcon className="h-4 w-4 mr-2" />
                Send SMS
              </button>
              <button
                onClick={() => setEmailModalOpen(true)}
                disabled={!customer.email}
                className="w-full btn-secondary text-left flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <EnvelopeIcon className="h-4 w-4 mr-2" />
                Send Email
              </button>
              <button onClick={() => navigate(`/crm`)} className="w-full btn-secondary text-left">
                View History
              </button>
            </div>
          </div>

          {/* AI Sales Assistant */}
          <AISalesAssistant customerId={customer.id} creditScore={customer.credit_score} />

          {/* Document Generator */}
          <DocumentGenerator customerId={customer.id} compact />
        </div>
      </div>

      {/* Edit Customer Modal */}
      {customer && (
        <CustomerModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveCustomer}
          customer={customer}
        />
      )}

      {/* Interested Vehicles Modal */}
      {customer && (
        <InterestedVehiclesModal
          isOpen={isInterestedVehiclesModalOpen}
          onClose={() => setIsInterestedVehiclesModalOpen(false)}
          customerId={customer.id}
          customerName={`${customer.first_name} ${customer.last_name}`}
          interestedVehicles={customer.interested_vehicles || []}
          onUpdate={fetchCustomer}
        />
      )}

      {/* Add Note Modal */}
      {noteModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Note</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  autoFocus
                  type="text"
                  value={noteModal.title}
                  onChange={(e) => setNoteModal((m) => ({ ...m, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Note title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Details</label>
                <textarea
                  rows={4}
                  value={noteModal.description}
                  onChange={(e) => setNoteModal((m) => ({ ...m, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Note details..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setNoteModal((m) => ({ ...m, open: false }))}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={!noteModal.title.trim() || noteModal.submitting}
                onClick={async () => {
                  setNoteModal((m) => ({ ...m, submitting: true }));
                  try {
                    await apiService.post('/crm/activities', {
                      activity_type: 'note_added',
                      title: noteModal.title,
                      description: noteModal.description,
                      customer_id: customer?.id,
                    });
                    toast.success('Note added');
                    setNoteModal({ open: false, title: '', description: '', submitting: false });
                  } catch {
                    toast.error('Failed to add note');
                    setNoteModal((m) => ({ ...m, submitting: false }));
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {noteModal.submitting ? 'Saving…' : 'Save Note'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send SMS Modal */}
      {customer && customer.phone && (
        <SendSMSModal
          isOpen={smsModalOpen}
          onClose={() => setSmsModalOpen(false)}
          customerId={customer.id}
          customerName={`${customer.first_name} ${customer.last_name}`}
          customerPhone={customer.phone}
        />
      )}

      {/* Send Email Modal */}
      {customer && customer.email && (
        <SendEmailModal
          isOpen={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          customerId={customer.id}
          customerName={`${customer.first_name} ${customer.last_name}`}
          customerEmail={customer.email}
        />
      )}

      {/* Schedule Follow-up Modal */}
      {followUpModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule Follow-up</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  autoFocus
                  type="text"
                  value={followUpModal.title}
                  onChange={(e) => setFollowUpModal((m) => ({ ...m, title: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Follow-up title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={followUpModal.dueDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setFollowUpModal((m) => ({ ...m, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-5">
              <button
                onClick={() => setFollowUpModal((m) => ({ ...m, open: false }))}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={
                  !followUpModal.title.trim() || !followUpModal.dueDate || followUpModal.submitting
                }
                onClick={async () => {
                  setFollowUpModal((m) => ({ ...m, submitting: true }));
                  try {
                    await apiService.post('/crm/tasks', {
                      title: followUpModal.title,
                      description: `Follow-up for ${customer?.first_name} ${customer?.last_name}`,
                      task_type: 'FOLLOW_UP',
                      priority: 'MEDIUM',
                      customer_id: customer?.id,
                      assigned_to_id: currentUser?.id,
                      due_date: `${followUpModal.dueDate}T00:00:00`,
                    });
                    toast.success('Follow-up scheduled');
                    setFollowUpModal({ open: false, title: '', dueDate: '', submitting: false });
                  } catch {
                    toast.error('Failed to schedule follow-up');
                    setFollowUpModal((m) => ({ ...m, submitting: false }));
                  }
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {followUpModal.submitting ? 'Scheduling…' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetailPage;
